/**
 * React Query Hooks for API Data Fetching
 * 
 * These hooks provide cached, deduplicated API calls with automatic
 * background refetching. They replace direct API calls in components.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../lib/queryClient';
import type { MyClub } from '../lib/clubs';
import type { TournamentStatus } from '../lib/tournaments';
import { findCachedTournamentSummary } from '../lib/prefetch';

// NOTE: lib/clubs and lib/tournaments (like the other lib/* API modules) statically
// import the aws-amplify SDK. They are imported *dynamically* inside the queryFns
// below so that merely using a hook here (e.g. the public useListPublicClubs on
// the landing page) does not pull Amplify into the entry chunk.

/**
 * Hook to fetch user's clubs with caching
 * Stale time: 60 seconds (clubs don't change often)
 */
export function useMyClubs(queryOptions?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.myClubs,
    queryFn: async () => {
      const { getMyClubs } = await import('../lib/clubs');
      return getMyClubs();
    },
    staleTime: 60 * 1000, // 1 minute
    enabled: queryOptions?.enabled ?? true,
  });
}

/**
 * The one rule for "may this person run tournaments for this club".
 *
 * Every TD surface must ask this and nothing else. It mirrors the backend's
 * `checkTournamentPermission`, which has always accepted OWNER or TD — the
 * frontend was the outlier.
 */
export function canManageClub(membership: Pick<MyClub, 'role'>): boolean {
  return membership.role === 'OWNER' || membership.role === 'TD';
}

/**
 * Hook to fetch the clubs the user can run tournaments for — OWNER **or** TD.
 * Uses the same cache as useMyClubs to avoid duplicate requests.
 *
 * This deliberately matches {@link useHasManageAccess} and
 * {@link useCanManageClub}. It used to filter to `OWNER` alone, which split
 * the app's answer to "can this person use TD mode" three ways: the header
 * badge said yes (OWNER||TD), `/td/tournament/:id` said yes (OWNER||TD), but
 * `/td` itself and the bottom-nav TD tab said no. A club TD therefore saw a
 * mode toggle that dropped them on "You don't own any clubs" — 24 attempts in
 * one beta session, the last of which ended the session.
 */
export function useManagedClubs() {
  const { data: clubs, ...rest } = useMyClubs();

  const managedClubs = clubs?.filter(canManageClub) ?? [];

  return {
    ...rest,
    data: managedClubs,
    managedClubs,
  };
}

/**
 * Hook to fetch tournaments for a specific club
 */
export function useClubTournaments(
  clubId: string | undefined,
  options?: { status?: string; limit?: number }
) {
  return useQuery({
    queryKey: queryKeys.clubTournaments(clubId ?? ''),
    queryFn: async () => {
      const { getClubTournaments } = await import('../lib/clubs');
      return getClubTournaments(clubId!, options);
    },
    enabled: !!clubId,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to fetch tournaments for multiple clubs in parallel
 * Returns combined results from all clubs
 */
export function useMultipleClubTournaments(
  clubs: MyClub[],
  options?: { limit?: number }
) {
  return useQuery({
    queryKey: ['multiClubTournaments', clubs.map(c => c.club.id), options],
    queryFn: async () => {
      if (clubs.length === 0) return [];

      const { getClubTournaments } = await import('../lib/clubs');
      const results = await Promise.all(
        clubs.map(async (myClub) => {
          try {
            const result = await getClubTournaments(myClub.club.id, options);
            return result.items.map(t => ({
              ...t,
              clubName: myClub.club.name,
            }));
          } catch (err) {
            console.error(`Failed to fetch tournaments for club ${myClub.club.id}:`, err);
            return [];
          }
        })
      );
      
      return results.flat();
    },
    enabled: clubs.length > 0,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to check if user is owner of a specific club
 * Uses cached myClubs data
 */
export function useIsClubOwner(clubId: string | undefined) {
  const { data: clubs, isLoading } = useMyClubs();

  const isOwner = clubs?.some(c => c.club.id === clubId && c.role === 'OWNER') ?? false;

  return {
    isOwner,
    isLoading,
  };
}

/**
 * Whether the user can manage a club's tournaments — a club OWNER or TD.
 * Mirrors the backend `checkTournamentPermission` (OWNER or TD), so a
 * delegated TD (not the owner) gets the same management affordances.
 */
export function useCanManageClub(clubId: string | undefined) {
  const { data: clubs, isLoading } = useMyClubs();

  const canManage = clubs?.some(c => c.club.id === clubId && canManageClub(c)) ?? false;

  return {
    canManage,
    isLoading,
  };
}

/**
 * Whether the user can manage at least one club — OWNER or TD anywhere, not
 * just clubs they own. Drives global "can this person reach TD mode at all"
 * checks (e.g. the header mode toggle); use {@link useCanManageClub} instead
 * when scoped to a specific club.
 */
export function useHasManageAccess() {
  const { data: clubs, isLoading } = useMyClubs();

  const hasManageAccess = clubs?.some(canManageClub) ?? false;

  return {
    hasManageAccess,
    isLoading,
  };
}

/**
 * Hook to fetch user's tournaments with caching
 * Stale time: 30 seconds (tournaments can change during events)
 */
export function useMyTournaments(options?: {
  status?: TournamentStatus[];
  clubId?: string;
  upcomingOnly?: boolean;
  limit?: number;
}) {
  return useQuery({
    queryKey: queryKeys.myTournaments(options),
    queryFn: async () => {
      const { getMyTournaments } = await import('../lib/tournaments');
      return getMyTournaments(options);
    },
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to fetch tournament details with caching
 */
export function useTournament(tournamentId: string | undefined) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: queryKeys.tournament(tournamentId ?? ''),
    queryFn: async () => {
      const { getTournament } = await import('../lib/tournaments');
      return getTournament(tournamentId!);
    },
    enabled: !!tournamentId,
    staleTime: 30 * 1000, // 30 seconds
    // Paint the header instantly from a summary already cached by the list the
    // user clicked from, then hydrate with the full fetch. Avoids a cold
    // skeleton on every navigation into a tournament. See lib/prefetch.ts.
    placeholderData: () => findCachedTournamentSummary(queryClient, tournamentId),
  });
}

/**
 * Hook to fetch user's registration for a tournament
 */
export function useMyRegistration(
  tournamentId: string | undefined,
  playerId: string | undefined
) {
  return useQuery({
    queryKey: ['myRegistration', tournamentId, playerId],
    queryFn: async () => {
      const { getMyRegistration } = await import('../lib/tournaments');
      return getMyRegistration(tournamentId!, playerId!);
    },
    enabled: !!tournamentId && !!playerId,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to fetch tournament dashboard (live match info)
 */
export function useMyTournamentDashboard(tournamentId: string | undefined) {
  return useQuery({
    queryKey: ['myTournamentDashboard', tournamentId],
    queryFn: async () => {
      const { getMyTournamentDashboard } = await import('../lib/tournaments');
      return getMyTournamentDashboard(tournamentId!);
    },
    enabled: !!tournamentId,
    staleTime: 10 * 1000, // 10 seconds (more frequent for live data)
  });
}

/**
 * Hook to fetch user profile with caching
 */
export function useMyProfile() {
  return useQuery({
    queryKey: queryKeys.profile,
    queryFn: async () => {
      const { getMyProfile } = await import('../lib/profile');
      const result = await getMyProfile();
      return result.success ? result.profile : null;
    },
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Hook to fetch user stats with caching
 */
export function useMyStats() {
  return useQuery({
    queryKey: ['myStats'],
    queryFn: async () => {
      const { getMyStats } = await import('../lib/stats');
      const result = await getMyStats();
      return result.success ? result.stats : null;
    },
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Hook to fetch tournament leaderboard data with caching
 */
export function useTournamentLeaderboard(
  tournamentId: string | undefined,
  currentPlayerId?: string
) {
  return useQuery({
    queryKey: ['tournamentLeaderboard', tournamentId, currentPlayerId],
    queryFn: async () => {
      const { getTournamentLeaderboard } = await import('../lib/tournaments');
      return getTournamentLeaderboard(tournamentId!, currentPlayerId);
    },
    enabled: !!tournamentId,
    staleTime: 15 * 1000, // 15 seconds (more frequent for live data)
  });
}

/**
 * Hook to fetch match history with caching
 */
export function useMyMatches(tournamentId: string | undefined) {
  return useQuery({
    queryKey: ['myMatches', tournamentId],
    queryFn: async () => {
      const { getMyMatches } = await import('../lib/tournaments');
      return getMyMatches(tournamentId!);
    },
    enabled: !!tournamentId,
    staleTime: 15 * 1000, // 15 seconds
  });
}

/**
 * Polling interval for public tournament views, based on tournament state.
 *
 * Public views can't use GraphQL subscriptions (they require Cognito auth;
 * public access is API-key), so they poll instead. At club scale this is
 * cheap and robust on flaky venue wifi. React Query pauses the interval
 * while the tab is backgrounded (refetchIntervalInBackground defaults off).
 *
 * - Live (group/knockout stage): 10s - scores appear within a battle's duration
 * - Pre/post event states: 60s - player counts and status changes
 * - Completed/cancelled or no data: no polling
 */
export function publicTournamentPollInterval(
  status: string | undefined
): number | false {
  switch (status) {
    case 'GROUP_STAGE_ACTIVE':
    case 'KNOCKOUT_STAGE_ACTIVE':
      return 10 * 1000;
    case 'REGISTRATION_OPEN':
    case 'REGISTRATION_CLOSED':
    case 'AWAITING_REVIEW':
      return 60 * 1000;
    default:
      return false;
  }
}

/**
 * Hook to fetch public tournament data with caching
 * Used for unauthenticated public views
 */
export function usePublicTournament(slug: string | undefined) {
  return useQuery({
    queryKey: queryKeys.publicTournament(slug ?? ''),
    queryFn: async () => {
      const { getPublicTournament } = await import('../lib/public-api');
      return getPublicTournament(slug!);
    },
    enabled: !!slug,
    staleTime: 15 * 1000, // 15 seconds (more frequent for live tournaments)
    refetchInterval: (query) =>
      publicTournamentPollInterval(query.state.data?.tournament?.status),
  });
}

// =============================================================================
// Public Club Hooks (unauthenticated access)
// =============================================================================

/**
 * Hook to fetch list of public clubs with caching
 * Used for unauthenticated club discovery
 * Stale time: 60 seconds (clubs don't change often)
 */
export function useListPublicClubs(
  options?: { limit?: number; nextToken?: string },
  queryOptions?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ['publicClubs', 'list', options],
    queryFn: async () => {
      const { listPublicClubs } = await import('../lib/public-club-api');
      return listPublicClubs(options);
    },
    staleTime: 60 * 1000, // 1 minute
    enabled: queryOptions?.enabled ?? true,
  });
}

/**
 * Hook to fetch public club data by slug with caching
 * Used for unauthenticated club detail view
 * Stale time: 60 seconds
 */
export function usePublicClub(slug: string | undefined, queryOptions?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['publicClub', slug],
    queryFn: async () => {
      const { getPublicClub } = await import('../lib/public-club-api');
      return getPublicClub(slug!);
    },
    enabled: (queryOptions?.enabled ?? true) && !!slug,
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Hook to search public clubs by name with caching
 * Used for unauthenticated club search
 * Stale time: 30 seconds (search results may change)
 */
export function useSearchPublicClubs(
  query: string,
  options?: { limit?: number; nextToken?: string },
  queryOptions?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ['publicClubs', 'search', query, options],
    queryFn: async () => {
      const { searchPublicClubs } = await import('../lib/public-club-api');
      return searchPublicClubs(query, options);
    },
    enabled: (queryOptions?.enabled ?? true) && query.length > 0,
    staleTime: 30 * 1000, // 30 seconds
  });
}

// =============================================================================
// Enhanced Stats Hooks (Requirements: 1.1-1.7, 3.1-3.7, 4.1-4.8)
// =============================================================================

/**
 * Hook to fetch tournament stats with finish type breakdowns
 * Requirements: 1.1-1.7, 2.1-2.5
 */
export function useTournamentStats(tournamentId: string | undefined) {
  return useQuery({
    queryKey: ['tournamentStats', tournamentId],
    queryFn: async () => {
      const { getTournamentStats } = await import('../lib/stats');
      const result = await getTournamentStats(tournamentId!);
      return result.success ? result.stats : null;
    },
    enabled: !!tournamentId,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to fetch season stats with caching
 * Requirements: 3.1-3.7
 */
export function useSeasonStats(seasonId: string | undefined, forceRefresh?: boolean) {
  return useQuery({
    queryKey: ['seasonStats', seasonId, forceRefresh],
    queryFn: async () => {
      const { getSeasonStats } = await import('../lib/stats');
      const result = await getSeasonStats(seasonId!, forceRefresh);
      return result.success ? result.stats : null;
    },
    enabled: !!seasonId,
    staleTime: 60 * 1000, // 1 minute (cached on backend)
  });
}

/**
 * Hook to fetch season leaderboard with specified view
 * Requirements: 4.1-4.8
 */
/** Query key intentionally excludes `enabled` — it's a hook-consumption detail
 * (whether to fire the request at all), not a cache dimension. Keeping the key
 * purely data-shaped lets seasonLeaderboardQueryOptions below double as the
 * key/fn a prefetch call mirrors to reliably warm this exact cache entry. */
export function seasonLeaderboardQueryKey(
  seasonId: string | undefined,
  view: import('@bbx/shared').LeaderboardView,
  dataOptions?: { minTournaments?: number; limit?: number }
) {
  return ['seasonLeaderboard', seasonId, view, dataOptions] as const;
}

/** Shared queryFn for a season leaderboard view — used by both the hook below
 * and the season-leaderboard prefetch helper (lib/prefetch.ts) so warming a
 * tab's cache ahead of a click and the hook's own fetch stay in lockstep. */
export async function fetchSeasonLeaderboard(
  seasonId: string,
  view: import('@bbx/shared').LeaderboardView,
  dataOptions?: { minTournaments?: number; limit?: number }
) {
  const { getSeasonLeaderboard } = await import('../lib/stats');
  const result = await getSeasonLeaderboard(
    seasonId,
    view,
    dataOptions?.minTournaments,
    dataOptions?.limit
  );
  // getSeasonLeaderboard swallows failures into { success: false, error },
  // which previously meant this query's `error` was never populated (every
  // failure — including "not authenticated" — looked identical to a
  // genuinely missing season). Throw so callers can distinguish them via
  // the query's error state (SeasonDetailPage uses this to redirect an
  // auth failure to the public /s/:id view instead of an unrecoverable
  // Retry — #16).
  if (!result.success) {
    throw new Error(result.error || 'Failed to get season leaderboard');
  }
  return result.leaderboard;
}

export const SEASON_LEADERBOARD_STALE_TIME = 30 * 1000; // 30 seconds

export function useSeasonLeaderboard(
  seasonId: string | undefined,
  view: import('@bbx/shared').LeaderboardView,
  options?: { minTournaments?: number; limit?: number; enabled?: boolean }
) {
  return useQuery({
    queryKey: seasonLeaderboardQueryKey(seasonId, view, options),
    queryFn: () => fetchSeasonLeaderboard(seasonId!, view, options),
    // `options.enabled` lets the caller (SeasonDetailPage) hold off firing
    // this until useSeasonStats has resolved at least once — see that
    // component's comment for the race this closes (a view requested before
    // the season-stats cache is warm gets a legitimately-empty response that
    // then reads as "no players" for that one tab, permanently, for
    // staleTime — because the backend's "cache not ready yet" sentinel is
    // indistinguishable from "season truly has zero stats").
    enabled: !!seasonId && (options?.enabled ?? true),
    staleTime: SEASON_LEADERBOARD_STALE_TIME,
  });
}

// =============================================================================
// Tournament Summary Hooks (unified-tournament-summary-view spec)
// Requirements: 9.1, 9.2, 9.5, 9.6
// =============================================================================

/**
 * Hook to fetch tournament summary data for the unified tournament view
 * Uses the public tournament API which provides all needed data in one call
 * 
 * Requirements: 9.1, 9.2, 9.5, 9.6
 * - 9.1: Display skeleton screens during data fetch
 * - 9.2: Show loading indicators for discrete sections
 * - 9.5: Display actionable error messages with retry options
 * - 9.6: Display cached tournament data with "Offline" indicator
 */
export function useTournamentSummary(slug: string | undefined) {
  return useQuery({
    queryKey: queryKeys.tournamentSummary(slug ?? ''),
    queryFn: async () => {
      const { getPublicTournament, calculateLiveMatchCount } = await import('../lib/public-api');
      const data = await getPublicTournament(slug!);
      
      // If there's an error, throw it so React Query handles it
      if (data.error) {
        throw new Error(data.error.message);
      }
      
      // Transform the public tournament data to the summary format
      return {
        publicData: data,
        liveMatchCount: calculateLiveMatchCount([...data.matches, ...data.bracket]),
      };
    },
    enabled: !!slug,
    staleTime: 15 * 1000, // 15 seconds (more frequent for live tournaments)
    // Live polling - see publicTournamentPollInterval for rationale
    refetchInterval: (query) =>
      publicTournamentPollInterval(query.state.data?.publicData?.tournament?.status),
    // Keep cached data available for offline viewing
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

// =============================================================================
// Season Hooks (clubs-seasons spec)
// Requirements: 10.4 - Display season info on tournament pages
// =============================================================================

/**
 * Hook to fetch a season by ID with caching
 * Used to display season info on tournament pages when a tournament has a seasonId
 * 
 * Requirements: 10.4 - Show season name on tournament detail page
 */
export function useSeasonById(seasonId: string | undefined | null) {
  return useQuery({
    queryKey: ['season', seasonId],
    queryFn: async () => {
      const { getSeason } = await import('../lib/seasons');
      return getSeason(seasonId!);
    },
    enabled: !!seasonId,
    staleTime: 60 * 1000, // 1 minute (seasons don't change often)
  });
}
